/**
 * Smart HubSpot form helpers for the contact form.
 * - Fill hidden_referrer from previous page URL (session bridge + document.referrer).
 * - Infer solution interest from a term in that URL and set related HubSpot fields.
 */

const STORAGE_KEY = 'arcadia_hs_referrer';
const FIELD_REFERRER = 'hidden_referrer';
const FIELD_SOLUTIONS = 'solutions_interested_in__website_inquiry';
const FIELD_SOLUTIONS_INFERRED = 'solutions_ix27m_interested_in__inferred';
const TTL_MS = 30 * 60 * 1000; // 30 minutes
const FORM_WAIT_MS = 15000;

/** URL substring (anywhere) → HubSpot option value for both solution fields. */
const SOLUTION_TERM_MAP = [
	{ term: 'ascent', value: 'Ascent' },
	{ term: 'carejourney', value: 'CareJourney' },
	{ term: 'lifescience', value: 'LifeScience' },
];

function getConfig() {
	const cfg = window.arcadiaHsSmartForm || {};
	return {
		formId: typeof cfg.formId === 'string' ? cfg.formId : '',
		contactUrl: typeof cfg.contactUrl === 'string' ? cfg.contactUrl : '',
	};
}

function hasAnalyticsConsent() {
	if (typeof window.getCkyConsent !== 'function') {
		return false;
	}
	try {
		const consent = window.getCkyConsent();
		return !!(consent && consent.categories && consent.categories.analytics);
	} catch (e) {
		return false;
	}
}

function normalizePathname(urlString) {
	try {
		const url = new URL(urlString, window.location.origin);
		let path = url.pathname || '/';
		if (path.length > 1 && path.endsWith('/')) {
			path = path.slice(0, -1);
		}
		return path;
	} catch (e) {
		return null;
	}
}

function isContactLink(anchor, contactPath) {
	if (!anchor || !contactPath) {
		return false;
	}
	if (anchor.classList && anchor.classList.contains('js-hs-referrer-cta')) {
		return true;
	}
	const href = anchor.getAttribute('href');
	if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
		return false;
	}
	const path = normalizePathname(href);
	return path !== null && path === contactPath;
}

function writeStoredReferrer(url) {
	if (!hasAnalyticsConsent()) {
		return;
	}
	try {
		localStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({ url: url, ts: Date.now() })
		);
	} catch (e) {
		// Quota / private mode — ignore.
	}
}

function clearStoredReferrer() {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch (e) {
		// ignore
	}
}

function peekStoredReferrer() {
	if (!hasAnalyticsConsent()) {
		return '';
	}
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return '';
		}
		const data = JSON.parse(raw);
		if (!data || typeof data.url !== 'string') {
			clearStoredReferrer();
			return '';
		}
		if (typeof data.ts === 'number' && Date.now() - data.ts > TTL_MS) {
			clearStoredReferrer();
			return '';
		}
		return data.url;
	} catch (e) {
		clearStoredReferrer();
		return '';
	}
}

function resolveReferrerValue() {
	const stored = peekStoredReferrer();
	if (stored) {
		return stored;
	}
	return typeof document.referrer === 'string' ? document.referrer : '';
}

function findFormRoot(formId) {
	if (!formId) {
		return null;
	}

	const frame = document.querySelector(
		'.hs-form-frame[data-form-id="' + formId + '"]'
	);
	if (frame) {
		const form = frame.querySelector('form');
		if (form) {
			return form;
		}
	}

	const scoped = document.querySelector(
		'[data-form-id="' + formId + '"] form'
	);
	if (scoped) {
		return scoped;
	}

	const referrerInputs = document.querySelectorAll(
		'input[name="' + FIELD_REFERRER + '"]'
	);
	for (let i = 0; i < referrerInputs.length; i++) {
		const form = referrerInputs[i].closest('form');
		if (form) {
			return form;
		}
	}

	const hsForm = document.querySelector('.hbspt-form form.hs-form');
	return hsForm || null;
}

function dispatchFieldEvents(input) {
	input.dispatchEvent(new Event('input', { bubbles: true }));
	input.dispatchEvent(new Event('change', { bubbles: true }));
}

function setTextLikeField(input, value) {
	if (!input || !value) {
		return;
	}
	input.value = value;
	input.setAttribute('value', value);
	dispatchFieldEvents(input);
}

function normalizeMatchKey(str) {
	return String(str || '')
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');
}

function getCheckboxLabelText(input) {
	const label =
		input.closest('label') ||
		(input.id
			? document.querySelector('label[for="' + input.id + '"]')
			: null) ||
		input.closest('.hs-form-checkbox');
	return label ? label.textContent || '' : '';
}

function findSolutionCheckbox(form, mappedValue, term) {
	const checkboxes = form.querySelectorAll(
		'input[type="checkbox"][name="' + FIELD_SOLUTIONS + '"]'
	);
	const wantExact = String(mappedValue);
	const wantKey = normalizeMatchKey(mappedValue);
	const termKey = normalizeMatchKey(term);

	for (let i = 0; i < checkboxes.length; i++) {
		const input = checkboxes[i];
		if (input.value === wantExact) {
			return input;
		}
	}

	for (let i = 0; i < checkboxes.length; i++) {
		const input = checkboxes[i];
		const valueKey = normalizeMatchKey(input.value);
		const labelKey = normalizeMatchKey(getCheckboxLabelText(input));
		if (
			valueKey === wantKey ||
			labelKey === wantKey ||
			valueKey.includes(termKey) ||
			labelKey.includes(termKey)
		) {
			return input;
		}
	}

	return null;
}

function setCheckboxChecked(input) {
	if (!input) {
		return;
	}
	if (input.checked) {
		return;
	}
	// HubSpot's native input.click() does not check these boxes; label click does.
	const label = input.closest('label');
	if (label) {
		label.click();
	}
	if (!input.checked) {
		input.checked = true;
		dispatchFieldEvents(input);
	}
}

function solutionsFromReferrerUrl(referrerUrl) {
	if (!referrerUrl) {
		return [];
	}
	const haystack = String(referrerUrl).toLowerCase();
	const matches = [];
	for (let i = 0; i < SOLUTION_TERM_MAP.length; i++) {
		const item = SOLUTION_TERM_MAP[i];
		if (haystack.indexOf(item.term) !== -1) {
			matches.push(item);
		}
	}
	return matches;
}

function applySolutionFields(form, referrerUrl) {
	const matches = solutionsFromReferrerUrl(referrerUrl);
	if (!matches.length) {
		return true;
	}

	const checkboxes = form.querySelectorAll(
		'input[type="checkbox"][name="' + FIELD_SOLUTIONS + '"]'
	);
	if (!checkboxes.length) {
		return false;
	}

	let checkedAny = false;
	for (let i = 0; i < matches.length; i++) {
		const checkbox = findSolutionCheckbox(
			form,
			matches[i].value,
			matches[i].term
		);
		if (checkbox) {
			setCheckboxChecked(checkbox);
			if (checkbox.checked) {
				checkedAny = true;
			}
		}
	}

	const inferred = form.querySelector(
		'input[name="' + FIELD_SOLUTIONS_INFERRED + '"]'
	);
	if (inferred) {
		setTextLikeField(inferred, matches[0].value);
	}

	return checkedAny || !matches.length;
}

let formFilled = false;

function fillFormFields(formId) {
	const form = findFormRoot(formId);
	if (!form) {
		return false;
	}

	const referrerInput = form.querySelector(
		'input[name="' + FIELD_REFERRER + '"]'
	);
	if (!referrerInput) {
		return false;
	}

	const referrerUrl = resolveReferrerValue();
	const matches = solutionsFromReferrerUrl(referrerUrl);

	// Wait for solution checkboxes when the referrer implies a solution.
	if (matches.length) {
		const checkboxes = form.querySelectorAll(
			'input[type="checkbox"][name="' + FIELD_SOLUTIONS + '"]'
		);
		if (!checkboxes.length) {
			return false;
		}
	}

	if (!formFilled) {
		if (referrerUrl) {
			setTextLikeField(referrerInput, referrerUrl);
			const solutionsReady = applySolutionFields(form, referrerUrl);
			if (!solutionsReady) {
				return false;
			}
		}
		clearStoredReferrer();
		formFilled = true;
	}
	return true;
}

let submitBound = false;

function bindSubmitRefresh(formId) {
	if (submitBound) {
		return;
	}
	submitBound = true;
	document.addEventListener(
		'submit',
		function (event) {
			const form = event.target;
			if (!(form instanceof HTMLFormElement)) {
				return;
			}
			if (formId) {
				const root = findFormRoot(formId);
				if (root && root !== form) {
					return;
				}
			}

			const referrerInput = form.querySelector(
				'input[name="' + FIELD_REFERRER + '"]'
			);
			const referrerUrl =
				(referrerInput && referrerInput.value) || resolveReferrerValue();

			if (referrerInput && !referrerInput.value && referrerUrl) {
				setTextLikeField(referrerInput, referrerUrl);
			}
			if (referrerUrl) {
				applySolutionFields(form, referrerUrl);
			}
		},
		true
	);
}

function waitForFormAndFill(formId) {
	if (!formId) {
		return;
	}

	if (fillFormFields(formId)) {
		bindSubmitRefresh(formId);
		return;
	}

	const started = Date.now();
	const observer = new MutationObserver(function () {
		if (fillFormFields(formId)) {
			observer.disconnect();
			bindSubmitRefresh(formId);
		} else if (Date.now() - started > FORM_WAIT_MS) {
			observer.disconnect();
		}
	});

	observer.observe(document.documentElement, {
		childList: true,
		subtree: true,
	});

	window.setTimeout(function () {
		observer.disconnect();
		if (fillFormFields(formId)) {
			bindSubmitRefresh(formId);
		}
	}, FORM_WAIT_MS);
}

function bindContactClickCapture(contactPath) {
	if (!contactPath) {
		return;
	}

	document.addEventListener(
		'click',
		function (event) {
			const target = event.target;
			if (!(target instanceof Element)) {
				return;
			}
			const anchor = target.closest('a[href]');
			if (!isContactLink(anchor, contactPath)) {
				return;
			}
			writeStoredReferrer(window.location.href);
		},
		true
	);
}

function bindConsentCleanup() {
	document.addEventListener('cookieyes_consent_update', function () {
		if (!hasAnalyticsConsent()) {
			clearStoredReferrer();
		}
	});
}

export function initHubspotSmartForm() {
	const { formId, contactUrl } = getConfig();
	const contactPath = contactUrl ? normalizePathname(contactUrl) : null;

	bindConsentCleanup();
	bindContactClickCapture(contactPath);
	waitForFormAndFill(formId);
}
